import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Input,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CountriesService } from '../../services/countries.service';
import { Country } from '../../models/country';
import { EMPTY_PHONE_VALUE, PhoneValue } from './phone-value';

// Generic fallback max digit-length used before a country is picked (or if the countries list
// failed to load) — long enough not to clip a real number, short enough to stop unbounded typing.
const DEFAULT_MAX_DIGITS = 15;

// Reusable phone-number input: a country/dial-code picker (flag + calling code, via the vendored
// flag-icons set — see src/styles/vendor/flag-icons) glued to a digits-only national-number
// field. Implements ControlValueAccessor + Validator so it drops straight into a Reactive Forms
// FormControl<PhoneValue> the same way a native <input> would — see
// AddDepartmentContactComponent's phone/mobilePhone controls for the intended usage.
@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => PhoneInputComponent),
      multi: true
    }
  ]
})
export class PhoneInputComponent implements OnInit, ControlValueAccessor, Validator {
  @Input() placeholder = '';

  value: PhoneValue = { ...EMPTY_PHONE_VALUE };
  disabled = false;

  countries: Country[] = [];
  filteredCountries: Country[] = [];
  searchTerm = '';

  isOpen = false;
  highlightIndex = -1;

  private countriesService = inject(CountriesService);
  private destroyRef = inject(DestroyRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private elementRef = inject(ElementRef<HTMLElement>);

  private onChange: (value: PhoneValue) => void = () => {};
  private onTouched: () => void = () => {};

  ngOnInit() {
    this.countriesService
      .getCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(countries => {
        this.countries = countries;
        this.filteredCountries = countries;
        this.changeDetectorRef.markForCheck();
      });
  }

  get selectedCountry(): Country | null {
    if (this.value.countryId === null) {
      return null;
    }
    return this.countries.find(country => country.id === this.value.countryId) ?? null;
  }

  get maxDigits(): number {
    return DEFAULT_MAX_DIGITS;
  }

  // --- ControlValueAccessor ---

  writeValue(value: PhoneValue | null): void {
    this.value = value ? { ...value } : { ...EMPTY_PHONE_VALUE };
  }

  registerOnChange(fn: (value: PhoneValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // --- Validator ---

  validate(_control: AbstractControl): ValidationErrors | null {
    const digits = this.value.number;
    const hasCountry = this.value.countryId !== null;

    if (!digits && !hasCountry) {
      // Both sides empty — pristine. Whether a phone number is required at all is the parent
      // form's own cross-field "at least one contact method" rule, not this validator's job.
      return null;
    }

    if (hasCountry && !digits) {
      // Country picked but no number typed — a dial code alone isn't a phone number.
      return { phoneNumberRequired: true };
    }

    if (digits && !hasCountry) {
      // Number typed but no country picked — the digits alone aren't dialable without a
      // country code. (No digit-count-vs-country range check here — country data for the exact
      // min/max varies too much in reliability to enforce as a hard error.)
      return { countryRequired: true };
    }

    return null;
  }

  // --- Number input ---

  onNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digitsOnly = input.value.replace(/\D/g, '').slice(0, this.maxDigits);
    input.value = digitsOnly;
    this.value = { ...this.value, number: digitsOnly };
    this.onChange({ ...this.value });
  }

  onNumberBlur(): void {
    this.onTouched();
  }

  // --- Country dropdown ---

  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = true;
    this.searchTerm = '';
    this.filteredCountries = this.countries;
    this.highlightIndex = this.countries.findIndex(country => country.id === this.value.countryId);
  }

  closeDropdown(): void {
    this.isOpen = false;
    this.highlightIndex = -1;
  }

  onSearchChange(term: string): void {
    this.searchTerm = term;
    const value = term.trim().toLowerCase();
    this.filteredCountries = !value
      ? this.countries
      : this.countries.filter(
          country =>
            country.name.toLowerCase().includes(value) ||
            country.iso2.toLowerCase().includes(value) ||
            country.dialCode.includes(value)
        );
    this.highlightIndex = this.filteredCountries.length > 0 ? 0 : -1;
  }

  selectCountry(country: Country): void {
    this.value = { ...this.value, countryId: country.id };
    this.onChange({ ...this.value });
    this.onTouched();
    this.closeDropdown();
  }

  // The ✕ sits at the end of the whole field (not next to the dial badge), so it reads as
  // "clear this field" rather than "clear just the country" — wipes both back to the empty
  // state in one click, same as a native input's clear affordance would.
  clear(): void {
    if (this.disabled) {
      return;
    }
    this.value = { ...EMPTY_PHONE_VALUE };
    this.onChange({ ...this.value });
    this.onTouched();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.filteredCountries.length === 0) {
        return;
      }
      const step = event.key === 'ArrowDown' ? 1 : -1;
      this.highlightIndex =
        (this.highlightIndex + step + this.filteredCountries.length) % this.filteredCountries.length;
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const country = this.filteredCountries[this.highlightIndex];
      if (country) {
        this.selectCountry(country);
      }
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeDropdown();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }
}
